import React, {
  useState,
  useEffect,
  useMemo,
  useCallback,
  useRef,
} from "react";
import { inventoryAPI } from "../../utils/inventoryAPI";
import { useAuth } from "../../context/AuthContext";
import { LoadingSpinner } from "../../components/Loaders";
import { EmptyState, ErrorState } from "../../components/EmptyStates";
import { showToast } from "../../components/Toast";
import { showConfirm } from "../../components/ConfirmModal";
import Pagination from "../../components/Pagination";
import {
  FiEdit2,
  FiTrash2,
  FiSave,
  FiX,
  FiPlus,
  FiSearch,
  FiFilter,
  FiDownload,
  FiUpload,
  FiRefreshCw,
} from "react-icons/fi";
import { useTranslation } from "react-i18next";
import "./Inventory.css";

// Format currency as RWF
const formatRWF = (amount) => {
  const num = parseFloat(amount || 0);
  return (
    new Intl.NumberFormat("en-RW", {
      minimumFractionDigits: 0,
      maximumFractionDigits: 0,
    }).format(num) + " RWF"
  );
};

function Inventory() {
  const { user } = useAuth();
  const [items, setItems] = useState([]);
  const [summary, setSummary] = useState(null);
  const [pagination, setPagination] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [currentPage, setCurrentPage] = useState(1);
  const [searchTerm, setSearchTerm] = useState("");
  const [debouncedSearch, setDebouncedSearch] = useState("");
  const [filterStatus, setFilterStatus] = useState("");
  const [filterSupplier, setFilterSupplier] = useState("");
  const [filterExpiryFrom, setFilterExpiryFrom] = useState("");
  const [filterExpiryTo, setFilterExpiryTo] = useState("");
  const [sortBy, setSortBy] = useState("");
  const [sortDir, setSortDir] = useState("asc");
  const [suppliers, setSuppliers] = useState([]);
  const [editingItem, setEditingItem] = useState(null);
  const [showEditModal, setShowEditModal] = useState(false);
  const [showAddModal, setShowAddModal] = useState(false);
  const [uploadingBulk, setUploadingBulk] = useState(false);
  const [bulkErrorState, setBulkErrorState] = useState(null);
  const bulkInputRef = React.useRef(null);
  const searchTimer = useRef(null);
  const { t } = useTranslation();

  const effectiveOrgId = useMemo(() => {
    if (user?.organization && typeof user.organization === "object") {
      return user.organization.id;
    }
    return user?.organization || null;
  }, [user]);

  const loadItems = useCallback(async () => {
    if (!effectiveOrgId) {
      setError(t("inventory.noOrganization", "No organization selected"));
      setLoading(false);
      return;
    }

    try {
      setLoading(true);
      const params = {
        page: currentPage,
        page_size: 20,
      };

      const trimmedSearch = debouncedSearch?.trim();
      if (trimmedSearch) {
        params.search = trimmedSearch;
      }
      if (filterStatus) {
        params.status = filterStatus;
      }
      if (filterSupplier) {
        params.supplier = filterSupplier;
      }
      if (filterExpiryFrom) {
        params.expiry_from = filterExpiryFrom;
      }
      if (filterExpiryTo) {
        params.expiry_to = filterExpiryTo;
      }
      if (sortBy) {
        params.sort_by = sortBy;
        params.sort_dir = sortDir;
      }

      const response = await inventoryAPI.listItems(effectiveOrgId, params);
      const data = response?.data || response;

      if (data?.items) {
        setItems(data.items);
        setPagination(data.pagination);
        setSummary(data.summary);
        // Use full supplier list from backend (not just current page)
        if (Array.isArray(data.suppliers)) {
          setSuppliers(data.suppliers);
        }
      } else {
        setItems([]);
        setPagination(null);
        setSummary(null);
      }
      setError(null);
    } catch (err) {
      setError(
        err?.message || t("inventory.loadFailed", "Failed to load inventory"),
      );
      setItems([]);
    } finally {
      setLoading(false);
    }
  }, [
    effectiveOrgId,
    currentPage,
    debouncedSearch,
    filterStatus,
    filterSupplier,
    filterExpiryFrom,
    filterExpiryTo,
    sortBy,
    sortDir,
  ]);

  // Debounce search input
  useEffect(() => {
    if (searchTimer.current) clearTimeout(searchTimer.current);
    searchTimer.current = setTimeout(() => {
      setDebouncedSearch(searchTerm);
      setCurrentPage(1);
    }, 400);
    return () => clearTimeout(searchTimer.current);
  }, [searchTerm]);

  // Reload whenever filters change
  useEffect(() => {
    if (!effectiveOrgId) return;
    loadItems();
  }, [
    effectiveOrgId,
    currentPage,
    debouncedSearch,
    filterStatus,
    filterSupplier,
    filterExpiryFrom,
    filterExpiryTo,
    sortBy,
    sortDir,
    loadItems,
  ]);

  // Helpers for sorting
  const handleSort = (field) => {
    if (sortBy === field) {
      setSortDir(sortDir === "asc" ? "desc" : "asc");
    } else {
      setSortBy(field);
      setSortDir("asc");
    }
    setCurrentPage(1);
  };

  const renderSortIcon = (field) => {
    if (sortBy !== field)
      return (
        <span style={{ opacity: 0.3, marginLeft: 4, fontSize: 12 }}>⇅</span>
      );
    return (
      <span style={{ marginLeft: 4, fontSize: 12 }}>
        {sortDir === "asc" ? "↑" : "↓"}
      </span>
    );
  };

  // Active filter count
  const activeFilterCount =
    (debouncedSearch ? 1 : 0) +
    (filterStatus ? 1 : 0) +
    (filterSupplier ? 1 : 0) +
    (filterExpiryFrom ? 1 : 0) +
    (filterExpiryTo ? 1 : 0);

  const clearFilters = () => {
    setSearchTerm("");
    setDebouncedSearch("");
    setFilterStatus("");
    setFilterSupplier("");
    setFilterExpiryFrom("");
    setFilterExpiryTo("");
    setSortBy("");
    setSortDir("asc");
    setCurrentPage(1);
  };

  const handleDelete = async (item) => {
    const confirmed = await showConfirm({
      title: t("inventory.deleteItemTitle", "Delete inventory item"),
      message: t(
        "inventory.deleteItemMessage",
        `Are you sure you want to delete "${item.product_name}"?`,
      ),
      confirmText: t("actions.deleteUser", "Delete"),
      cancelText: t("actions.cancel", "Cancel"),
      type: "danger",
    });
    if (!confirmed) return;

    try {
      await inventoryAPI.deleteItem(effectiveOrgId, item.id);
      showToast.success(
        t("inventory.itemDeleted", "Item deleted successfully"),
      );
      loadItems();
    } catch (err) {
      showToast.error(
        err?.message || t("inventory.deleteFailed", "Failed to delete item"),
      );
    }
  };

  const handleEdit = (item) => {
    setEditingItem({ ...item });
    setShowEditModal(true);
  };

  const handleSaveEdit = async () => {
    if (!editingItem || !effectiveOrgId) return;

    try {
      // Ensure numeric fields are properly formatted
      const payload = {
        ...editingItem,
        units_bought: editingItem.units_bought
          ? parseFloat(editingItem.units_bought)
          : 0,
        units_sold: editingItem.units_sold
          ? parseFloat(editingItem.units_sold)
          : 0,
        unit_price: editingItem.unit_price
          ? parseFloat(editingItem.unit_price)
          : 0,
        low_stock_threshold: editingItem.low_stock_threshold
          ? parseInt(editingItem.low_stock_threshold)
          : 10,
        expiry_threshold_days: editingItem.expiry_threshold_days
          ? parseInt(editingItem.expiry_threshold_days)
          : 7,
      };

      await inventoryAPI.updateItem(effectiveOrgId, editingItem.id, payload);
      showToast.success(
        t("inventory.itemUpdated", "Item updated successfully"),
      );
      setShowEditModal(false);
      setEditingItem(null);
      loadItems();
    } catch (err) {
      const errorMsg =
        err?.response?.data?.details ||
        err?.response?.data?.message ||
        err?.message ||
        t("inventory.updateFailed", "Failed to update item");
      showToast.error(errorMsg);
    }
  };

  const handleAdd = async (itemData) => {
    if (!effectiveOrgId) return;

    try {
      await inventoryAPI.createItem(effectiveOrgId, itemData);
      showToast.success(t("inventory.itemAdded", "Item added successfully"));
      setShowAddModal(false);
      loadItems();
    } catch (err) {
      showToast.error(
        err?.message || t("inventory.addFailed", "Failed to add item"),
      );
    }
  };

  const downloadTemplate = async () => {
    if (!effectiveOrgId) return;

    try {
      const blob = await inventoryAPI.downloadTemplate(effectiveOrgId);
      const url = URL.createObjectURL(blob);
      const a = document.createElement("a");
      a.href = url;
      a.download = "inventory_template.csv";
      a.click();
      URL.revokeObjectURL(url);
      showToast.success(
        t("inventory.templateDownloaded", "Template downloaded"),
      );
    } catch (err) {
      showToast.error(
        err?.message ||
          t("inventory.downloadTemplateFailed", "Failed to download template"),
      );
    }
  };

  const exportFiltered = async () => {
    if (!effectiveOrgId) return;

    try {
      const params = {};
      const trimmedSearch = searchTerm?.trim();
      if (trimmedSearch) params.search = trimmedSearch;
      if (filterStatus) params.status = filterStatus;
      if (filterSupplier) params.supplier = filterSupplier;
      if (filterExpiryFrom) params.expiry_from = filterExpiryFrom;
      if (filterExpiryTo) params.expiry_to = filterExpiryTo;
      if (sortBy) params.sort_by = sortBy;
      if (sortDir) params.sort_dir = sortDir;

      const blob = await inventoryAPI.exportItems(effectiveOrgId, params);
      const url = URL.createObjectURL(blob);
      const a = document.createElement("a");
      a.href = url;
      a.download = "inventory_export.csv";
      a.click();
      URL.revokeObjectURL(url);
      showToast.success(
        t(
          "inventory.exportSuccess",
          "Exported inventory CSV for current filters",
        ),
      );
    } catch (err) {
      showToast.error(
        err?.message ||
          t("inventory.exportFailed", "Failed to export inventory"),
      );
    }
  };

  const handleBulkUpload = async (e) => {
    const file = e?.target?.files?.[0];
    if (!file || !effectiveOrgId) return;

    try {
      setUploadingBulk(true);
      const text = await file.text();
      const lines = text.split(/\r?\n/).filter((l) => l.trim());
      if (lines.length < 2) {
        showToast.warning(
          t(
            "inventory.csvTooShort",
            "CSV must have a header row and at least one data row",
          ),
        );
        return;
      }

      const header = lines[0].split(",").map((h) => h.trim());
      const rows = [];

      for (let i = 1; i < lines.length; i++) {
        const values = lines[i].split(",").map((v) => v.trim());
        const row = {};
        header.forEach((key, idx) => {
          row[key] = values[idx] || "";
        });
        if (Object.values(row).some((v) => v)) {
          rows.push(row);
        }
      }

      if (rows.length === 0) {
        showToast.warning(
          t("inventory.noRowsFound", "No valid data rows found"),
        );
        return;
      }

      const response = await inventoryAPI.bulkUpload(effectiveOrgId, { rows });
      const count = response?.data?.length || rows.length;
      showToast.success(
        t("inventory.bulkSuccess", `${count} item(s) uploaded successfully`),
      );
      if (bulkInputRef.current) bulkInputRef.current.value = "";
      loadItems();
    } catch (err) {
      const details = err?.response?.data?.details || {};

      // Prefer new structured errors format: [{ row, field, message }]
      let structured = Array.isArray(details.errors) ? details.errors : null;

      // Backwards compatibility: convert legacy validation_errors -> structured
      if (!structured && Array.isArray(details.validation_errors)) {
        const converted = [];
        details.validation_errors.forEach((rowErr) => {
          const rowIndex = rowErr.row_index;
          (rowErr.errors || []).forEach((msg) => {
            const str = String(msg);
            const [fieldPart, ...rest] = str.split(":");
            const field = fieldPart ? fieldPart.trim() : "unknown";
            const message = rest.join(":").trim() || str;
            converted.push({
              row: rowIndex,
              field,
              message,
            });
          });
        });
        structured = converted;
      }

      if (structured && structured.length > 0) {
        setBulkErrorState({
          errors: structured,
          createdCount: details.created_count || 0,
          errorCount: details.error_count || structured.length,
        });
        showToast.error(
          t(
            "inventory.bulkValidationFailed",
            "Bulk upload validation failed. See detailed errors.",
          ),
        );
      } else {
        const msg =
          err?.message || t("inventory.uploadFailed", "Upload failed");
        showToast.error(msg);
      }
    } finally {
      setUploadingBulk(false);
    }
  };

  const getStatusBadgeClass = (status) => {
    const statusMap = {
      FINISHED: "status-finished",
      URGENT: "status-urgent",
      LOW_STOCK: "status-low-stock",
      GIP: "status-gip",
      DEAD: "status-dead",
    };
    return statusMap[status] || "status-gip";
  };

  const getStatusColor = (status) => {
    const colorMap = {
      FINISHED: "#ef4444", // Red
      URGENT: "#a855f7", // Violet
      LOW_STOCK: "#f59e0b", // Orange
      GIP: "#10b981", // Green
      DEAD: "#eab308", // Yellow
    };
    return colorMap[status] || "#6b7280";
  };

  if (loading && items.length === 0) {
    return (
      <div className="page-content">
        <div className="card">
          <LoadingSpinner />
        </div>
      </div>
    );
  }

  if (error && items.length === 0) {
    return (
      <div className="page-content">
        <ErrorState error={error} onRetry={loadItems} />
      </div>
    );
  }

  return (
    <div className="page-content" style={{ minWidth: 0 }}>
      {/* 1️⃣ Filters & Search Section */}
      <div className="card" style={{ minWidth: 0 }}>
        <div
          style={{
            display: "flex",
            justifyContent: "space-between",
            alignItems: "center",
            marginBottom: "20px",
            flexWrap: "wrap",
            gap: "10px",
          }}
        >
          <h2 style={{ fontSize: "20px", fontWeight: "600", margin: 0 }}>
            {t("inventory.filtersTitle", "Filters & Search")}
            {activeFilterCount > 0 && (
              <span
                style={{
                  marginLeft: "10px",
                  padding: "2px 10px",
                  borderRadius: "12px",
                  backgroundColor: "var(--primary-blue, #4f46e5)",
                  color: "white",
                  fontSize: "12px",
                  fontWeight: "600",
                }}
              >
                {activeFilterCount} active
              </span>
            )}
          </h2>
          {activeFilterCount > 0 && (
            <button
              className="btn btn-secondary btn-sm"
              onClick={clearFilters}
              style={{ display: "flex", alignItems: "center", gap: "6px" }}
            >
              <FiX /> {t("inventory.clearFilters", "Clear Filters")}
            </button>
          )}
        </div>
        <div className="inventory-filters">
          <div>
            <label
              style={{
                display: "block",
                marginBottom: "8px",
                fontWeight: "500",
                fontSize: "14px",
              }}
            >
              {t("inventory.searchLabel", "Search Products")}
            </label>
            <div style={{ position: "relative" }}>
              <FiSearch
                style={{
                  position: "absolute",
                  left: "12px",
                  top: "50%",
                  transform: "translateY(-50%)",
                  color: "var(--text-grey)",
                }}
              />
              <input
                type="text"
                placeholder={t(
                  "inventory.searchPlaceholder",
                  "Search by name, batch, supplier...",
                )}
                value={searchTerm}
                onChange={(e) => setSearchTerm(e.target.value)}
                style={{
                  padding: "10px 12px 10px 40px",
                  borderRadius: "8px",
                  border: "1px solid var(--border-grey)",
                  width: "100%",
                  fontSize: "14px",
                }}
              />
            </div>
            <small
              style={{
                color: "var(--text-grey)",
                fontSize: "12px",
                marginTop: "4px",
                display: "block",
              }}
            >
              {t(
                "inventory.searchHelp",
                "Search by product name, batch number, supplier, or storage location",
              )}
            </small>
          </div>

          <div>
            <label
              style={{
                display: "block",
                marginBottom: "8px",
                fontWeight: "500",
                fontSize: "14px",
              }}
            >
              {t("inventory.statusFilter", "Status Filter")}
            </label>
            <select
              value={filterStatus}
              onChange={(e) => {
                setFilterStatus(e.target.value);
                setCurrentPage(1);
              }}
              style={{
                padding: "10px 12px",
                borderRadius: "8px",
                border: "1px solid var(--border-grey)",
                width: "100%",
                fontSize: "14px",
              }}
            >
              <option value="">{t("inventory.allStatus", "All Status")}</option>
              <option value="FINISHED">
                {t("inventory.finished", "Finished")}
              </option>
              <option value="URGENT">{t("inventory.urgent", "Urgent")}</option>
              <option value="LOW_STOCK">
                {t("inventory.lowStock", "Low Stock")}
              </option>
              <option value="GIP">{t("inventory.gip", "GIP")}</option>
              <option value="DEAD">
                {t("inventory.deadStock", "Dead Stock")}
              </option>
            </select>
            <small
              style={{
                color: "var(--text-grey)",
                fontSize: "12px",
                marginTop: "4px",
                display: "block",
              }}
            >
              {t(
                "inventory.statusHelp",
                "Classifies products based on stock level and expiry risk",
              )}
            </small>
          </div>

          <div>
            <label
              style={{
                display: "block",
                marginBottom: "8px",
                fontWeight: "500",
                fontSize: "14px",
              }}
            >
              {t("inventory.supplierFilter", "Supplier Filter")}
            </label>
            <select
              value={filterSupplier}
              onChange={(e) => {
                setFilterSupplier(e.target.value);
                setCurrentPage(1);
              }}
              style={{
                padding: "10px 12px",
                borderRadius: "8px",
                border: "1px solid var(--border-grey)",
                width: "100%",
                fontSize: "14px",
              }}
            >
              <option value="">
                {t("inventory.allSuppliers", "All Suppliers")}
              </option>
              {suppliers.map((sup) => (
                <option key={sup} value={sup}>
                  {sup}
                </option>
              ))}
            </select>
            <small
              style={{
                color: "var(--text-grey)",
                fontSize: "12px",
                marginTop: "4px",
                display: "block",
              }}
            >
              {t("inventory.supplierHelp", "Filter products by their supplier")}
            </small>
          </div>

          <div>
            <label
              style={{
                display: "block",
                marginBottom: "8px",
                fontWeight: "500",
                fontSize: "14px",
              }}
            >
              {t("inventory.expiryFrom", "Expiry Date From")}
            </label>
            <input
              type="date"
              value={filterExpiryFrom}
              onChange={(e) => {
                setFilterExpiryFrom(e.target.value);
                setCurrentPage(1);
              }}
              style={{
                padding: "10px 12px",
                borderRadius: "8px",
                border: "1px solid var(--border-grey)",
                width: "100%",
                fontSize: "14px",
              }}
            />
            <small
              style={{
                color: "var(--text-grey)",
                fontSize: "12px",
                marginTop: "4px",
                display: "block",
              }}
            >
              {t("inventory.expiryHelp", "Filter by expiration date range")}
            </small>
          </div>

          <div>
            <label
              style={{
                display: "block",
                marginBottom: "8px",
                fontWeight: "500",
                fontSize: "14px",
              }}
            >
              {t("inventory.expiryTo", "Expiry Date To")}
            </label>
            <input
              type="date"
              value={filterExpiryTo}
              onChange={(e) => {
                setFilterExpiryTo(e.target.value);
                setCurrentPage(1);
              }}
              style={{
                padding: "10px 12px",
                borderRadius: "8px",
                border: "1px solid var(--border-grey)",
                width: "100%",
                fontSize: "14px",
              }}
            />
            <small
              style={{
                color: "var(--text-grey)",
                fontSize: "12px",
                marginTop: "4px",
                display: "block",
              }}
            >
              {t("inventory.expiryToHelp", "End date for expiration filter")}
            </small>
          </div>
        </div>

        <div
          style={{
            display: "flex",
            gap: "12px",
            flexWrap: "wrap",
            marginTop: "16px",
          }}
        >
          <button
            className="btn btn-primary btn-sm"
            onClick={() => setShowAddModal(true)}
            style={{ display: "flex", alignItems: "center", gap: "6px" }}
          >
            <FiPlus /> {t("inventory.addItem", "Add Item")}
          </button>

          <button
            className="btn btn-secondary btn-sm"
            onClick={downloadTemplate}
            style={{ display: "flex", alignItems: "center", gap: "6px" }}
          >
            <FiDownload />{" "}
            {t("inventory.downloadTemplate", "Download Template")}
          </button>

          <button
            className="btn btn-secondary btn-sm"
            onClick={exportFiltered}
            style={{ display: "flex", alignItems: "center", gap: "6px" }}
          >
            <FiDownload /> {t("inventory.exportCsv")}
          </button>

          <label
            className="btn btn-secondary btn-sm"
            style={{
              display: "flex",
              alignItems: "center",
              gap: "6px",
              cursor: "pointer",
            }}
          >
            <FiUpload />{" "}
            {uploadingBulk
              ? t("inventory.uploadingCsv", "Uploading...")
              : t("inventory.uploadCsv", "Upload CSV")}
            <input
              ref={bulkInputRef}
              type="file"
              accept=".csv"
              style={{ display: "none" }}
              onChange={handleBulkUpload}
              disabled={uploadingBulk}
            />
          </label>

          <button
            className="btn btn-secondary btn-sm"
            onClick={loadItems}
            style={{ display: "flex", alignItems: "center", gap: "6px" }}
          >
            <FiRefreshCw /> {t("common.refresh", "Refresh")}
          </button>
        </div>
      </div>

      {/* 2️⃣ Statistics Cards Section */}
      {summary && (
        <div className="inventory-stats">
          <div
            className="card"
            style={{ padding: "20px", textAlign: "center" }}
          >
            <div
              style={{
                fontSize: "28px",
                fontWeight: "700",
                color: "var(--primary-blue)",
              }}
            >
              {formatRWF(summary.total_inventory_value || 0)}
            </div>
            <div
              style={{
                color: "var(--text-grey)",
                marginTop: "8px",
                fontSize: "14px",
              }}
            >
              {t("inventory.totalValue", "Total Value")}
            </div>
          </div>
          <div
            className="card"
            style={{ padding: "20px", textAlign: "center" }}
          >
            <div
              style={{ fontSize: "28px", fontWeight: "700", color: "#a855f7" }}
            >
              {summary.urgent_count || 0}
            </div>
            <div
              style={{
                color: "var(--text-grey)",
                marginTop: "8px",
                fontSize: "14px",
              }}
            >
              {t("inventory.urgent", "Urgent")}
            </div>
          </div>
          <div
            className="card"
            style={{ padding: "20px", textAlign: "center" }}
          >
            <div
              style={{ fontSize: "28px", fontWeight: "700", color: "#ef4444" }}
            >
              {summary.finished_count || 0}
            </div>
            <div
              style={{
                color: "var(--text-grey)",
                marginTop: "8px",
                fontSize: "14px",
              }}
            >
              {t("inventory.finished", "Finished")}
            </div>
          </div>
          <div
            className="card"
            style={{ padding: "20px", textAlign: "center" }}
          >
            <div
              style={{ fontSize: "28px", fontWeight: "700", color: "#10b981" }}
            >
              {summary.gip_count || 0}
            </div>
            <div
              style={{
                color: "var(--text-grey)",
                marginTop: "8px",
                fontSize: "14px",
              }}
            >
              {t("inventory.gip", "GIP")}
            </div>
          </div>
          <div
            className="card"
            style={{ padding: "20px", textAlign: "center" }}
          >
            <div
              style={{ fontSize: "28px", fontWeight: "700", color: "#eab308" }}
            >
              {summary.dead_count || 0}
            </div>
            <div
              style={{
                color: "var(--text-grey)",
                marginTop: "8px",
                fontSize: "14px",
              }}
            >
              {t("inventory.deadStock", "Dead Stock")}
            </div>
          </div>
          <div
            className="card"
            style={{ padding: "20px", textAlign: "center" }}
          >
            <div
              style={{ fontSize: "28px", fontWeight: "700", color: "#f59e0b" }}
            >
              {summary.low_stock_count || 0}
            </div>
            <div
              style={{
                color: "var(--text-grey)",
                marginTop: "8px",
                fontSize: "14px",
              }}
            >
              {t("inventory.lowStock", "Low Stock")}
            </div>
          </div>
        </div>
      )}

      {/* 3️⃣ Products Table Section */}
      <div className="card">
        <h2
          style={{ marginBottom: "20px", fontSize: "20px", fontWeight: "600" }}
        >
          {t("inventory.productsTitle", "Products")}
        </h2>
        {items.length === 0 ? (
          <EmptyState
            title={t("inventory.emptyTitle", "No inventory items")}
            message={t(
              "inventory.emptyMessage",
              "Add items manually or upload a CSV file to get started.",
            )}
          />
        ) : (
          <>
            <div className="inventory-table-wrapper">
              <table className="data-table">
                <thead>
                  <tr>
                    <th
                      onClick={() => handleSort("product_name")}
                      style={{ cursor: "pointer", userSelect: "none" }}
                    >
                      {t("inventory.productCol", "Product")}{" "}
                      {renderSortIcon("product_name")}
                    </th>
                    <th
                      onClick={() => handleSort("supplier")}
                      style={{ cursor: "pointer", userSelect: "none" }}
                    >
                      {t("inventory.supplierCol", "Supplier")}{" "}
                      {renderSortIcon("supplier")}
                    </th>
                    <th
                      onClick={() => handleSort("batch_number")}
                      style={{ cursor: "pointer", userSelect: "none" }}
                    >
                      {t("inventory.batchCol", "Batch")}{" "}
                      {renderSortIcon("batch_number")}
                    </th>
                    <th
                      onClick={() => handleSort("date_time_added")}
                      style={{ cursor: "pointer", userSelect: "none" }}
                    >
                      {t("inventory.dateAddedCol", "Date Added")}{" "}
                      {renderSortIcon("date_time_added")}
                    </th>
                    <th
                      onClick={() => handleSort("expiration_date")}
                      style={{ cursor: "pointer", userSelect: "none" }}
                    >
                      {t("inventory.expirationCol", "Expiration")}{" "}
                      {renderSortIcon("expiration_date")}
                    </th>
                    <th
                      onClick={() => handleSort("units_bought")}
                      style={{ cursor: "pointer", userSelect: "none" }}
                    >
                      {t("inventory.boughtCol", "Bought")}{" "}
                      {renderSortIcon("units_bought")}
                    </th>
                    <th
                      onClick={() => handleSort("units_sold")}
                      style={{ cursor: "pointer", userSelect: "none" }}
                    >
                      {t("inventory.soldCol", "Sold")}{" "}
                      {renderSortIcon("units_sold")}
                    </th>
                    <th
                      onClick={() => handleSort("remaining_quantity")}
                      style={{ cursor: "pointer", userSelect: "none" }}
                    >
                      {t("inventory.remainingCol", "Remaining")}{" "}
                      {renderSortIcon("remaining_quantity")}
                    </th>
                    <th
                      onClick={() => handleSort("low_stock_threshold")}
                      style={{ cursor: "pointer", userSelect: "none" }}
                    >
                      {t(
                        "inventory.lowStockThresholdCol",
                        "Low Stock Threshold",
                      )}{" "}
                      {renderSortIcon("low_stock_threshold")}
                    </th>
                    <th
                      onClick={() => handleSort("expiry_threshold_days")}
                      style={{ cursor: "pointer", userSelect: "none" }}
                    >
                      {t("inventory.expiryThresholdCol", "Expiry Threshold")}{" "}
                      {renderSortIcon("expiry_threshold_days")}
                    </th>
                    <th
                      onClick={() => handleSort("unit_price")}
                      style={{ cursor: "pointer", userSelect: "none" }}
                    >
                      {t("inventory.unitPriceCol", "Unit Price")}{" "}
                      {renderSortIcon("unit_price")}
                    </th>
                    <th
                      onClick={() => handleSort("total_amount")}
                      style={{ cursor: "pointer", userSelect: "none" }}
                    >
                      {t("inventory.totalValueCol", "Total Value")}{" "}
                      {renderSortIcon("total_amount")}
                    </th>
                    <th
                      onClick={() => handleSort("status")}
                      style={{ cursor: "pointer", userSelect: "none" }}
                    >
                      {t("common.status", "Status")} {renderSortIcon("status")}
                    </th>
                    <th>{t("common.actions", "Actions")}</th>
                  </tr>
                </thead>
                <tbody>
                  {items.map((item) => (
                    <tr key={item.id}>
                      <td style={{ fontWeight: "500" }}>{item.product_name}</td>
                      <td>{item.supplier || "-"}</td>
                      <td>{item.batch_number || "-"}</td>
                      <td>
                        {item.date_time_added
                          ? new Date(item.date_time_added).toLocaleDateString()
                          : "-"}
                      </td>
                      <td>
                        {item.expiration_date
                          ? new Date(item.expiration_date).toLocaleDateString()
                          : "-"}
                      </td>
                      <td>{parseFloat(item.units_bought || 0).toFixed(2)}</td>
                      <td>{parseFloat(item.units_sold || 0).toFixed(2)}</td>
                      <td style={{ fontWeight: "600" }}>
                        {parseFloat(item.remaining_quantity || 0).toFixed(2)}
                      </td>
                      <td>{item.low_stock_threshold || "-"}</td>
                      <td>
                        {item.expiry_threshold_days || "-"}{" "}
                        {item.expiry_threshold_days
                          ? t("common.days", "days")
                          : ""}
                      </td>
                      <td>{formatRWF(item.unit_price || 0)}</td>
                      <td style={{ fontWeight: "600" }}>
                        {formatRWF(item.total_amount || 0)}
                      </td>
                      <td>
                        <span
                          className={`status-badge ${getStatusBadgeClass(item.status)}`}
                          style={{
                            backgroundColor: getStatusColor(item.status) + "20",
                            color: getStatusColor(item.status),
                            borderColor: getStatusColor(item.status),
                            padding: "4px 12px",
                            borderRadius: "12px",
                            fontSize: "12px",
                            fontWeight: "600",
                          }}
                        >
                          {item.status?.replace("_", " ") || "-"}
                        </span>
                      </td>
                      <td>
                        <button
                          className="btn btn-primary btn-sm"
                          onClick={() => handleEdit(item)}
                          style={{ marginRight: "8px" }}
                        >
                          <FiEdit2 /> {t("actions.edit", "Edit")}
                        </button>
                        <button
                          className="btn btn-secondary btn-sm"
                          onClick={() => handleDelete(item)}
                        >
                          <FiTrash2 />
                        </button>
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
            {pagination && (
              <Pagination
                pagination={pagination}
                onPageChange={setCurrentPage}
              />
            )}
          </>
        )}
      </div>

      {/* Edit Modal */}
      {showEditModal && editingItem && (
        <InventoryItemModal
          item={editingItem}
          onClose={() => {
            setShowEditModal(false);
            setEditingItem(null);
          }}
          onSave={handleSaveEdit}
          onChange={(field, value) => {
            setEditingItem({ ...editingItem, [field]: value });
          }}
        />
      )}

      {/* Add Modal */}
      {showAddModal && (
        <InventoryItemModal
          item={null}
          onClose={() => setShowAddModal(false)}
          onSave={handleAdd}
          onChange={() => {}}
        />
      )}

      {/* Bulk upload error details modal */}
      {bulkErrorState && (
        <BulkUploadErrorModal
          state={bulkErrorState}
          onClose={() => {
            setBulkErrorState(null);
            if (bulkInputRef.current) bulkInputRef.current.value = "";
          }}
        />
      )}
    </div>
  );
}

function InventoryItemModal({ item, onClose, onSave, onChange }) {
  const [formData, setFormData] = useState(
    item || {
      product_name: "",
      supplier: "",
      batch_number: "",
      date_time_added: new Date().toISOString().slice(0, 16),
      expiration_date: "",
      units_bought: 0,
      units_sold: 0,
      unit_price: 0,
      low_stock_threshold: 10,
      expiry_threshold_days: 7,
      storage_location: "",
      notes: "",
    },
  );
  const [saving, setSaving] = useState(false);
  const [fieldErrors, setFieldErrors] = useState({});

  const handleChange = (field, value) => {
    const updated = { ...formData, [field]: value };
    setFormData(updated);
    if (onChange) onChange(field, value);
  };

  const handleSubmit = async (e) => {
    e.preventDefault();

    const errors = {};

    if (!formData.product_name || !String(formData.product_name).trim()) {
      errors.product_name = "Product name is required.";
    }

    const unitsBought = parseFloat(formData.units_bought || 0);
    const unitsSold = parseFloat(formData.units_sold || 0);

    if (Number.isNaN(unitsBought) || unitsBought < 0) {
      errors.units_bought = "Units bought must be a non‑negative number.";
    }

    if (Number.isNaN(unitsSold) || unitsSold < 0) {
      errors.units_sold = "Units sold must be a non‑negative number.";
    } else if (unitsSold > unitsBought) {
      errors.units_sold = "Units sold cannot exceed units bought.";
    }

    const lowStock = parseFloat(formData.low_stock_threshold || 0);
    if (Number.isNaN(lowStock) || lowStock <= 0) {
      errors.low_stock_threshold =
        "Low stock threshold must be greater than zero.";
    }

    const expiryThreshold = parseFloat(formData.expiry_threshold_days || 0);
    if (Number.isNaN(expiryThreshold) || expiryThreshold <= 0) {
      errors.expiry_threshold_days =
        "Expiry threshold must be greater than zero.";
    }

    if (formData.expiration_date) {
      const todayStr = new Date().toISOString().slice(0, 10);
      if (formData.expiration_date < todayStr) {
        errors.expiration_date =
          "Expiration date must be today or a future date.";
      }
    }

    if (Object.keys(errors).length > 0) {
      setFieldErrors(errors);
      showToast.error("Please fix the highlighted fields.");
      return;
    }

    setFieldErrors({});

    setSaving(true);
    try {
      const payload = {
        ...formData,
        units_bought: parseFloat(formData.units_bought) || 0,
        units_sold: parseFloat(formData.units_sold) || 0,
        unit_price: parseFloat(formData.unit_price) || 0,
        low_stock_threshold: parseInt(formData.low_stock_threshold) || 10,
        expiry_threshold_days: parseInt(formData.expiry_threshold_days) || 7,
        expiration_date: formData.expiration_date || null,
        date_time_added: formData.date_time_added || new Date().toISOString(),
      };
      await onSave(payload);
    } catch (err) {
      showToast.error(err?.message || "Failed to save");
    } finally {
      setSaving(false);
    }
  };

  return (
    <div
      style={{
        position: "fixed",
        top: 0,
        left: 0,
        right: 0,
        bottom: 0,
        background: "rgba(0,0,0,0.5)",
        display: "flex",
        alignItems: "center",
        justifyContent: "center",
        zIndex: 1000,
        padding: "20px",
        overflowY: "auto",
      }}
      onClick={onClose}
    >
      <div
        className="card inventory-modal"
        style={{
          maxWidth: "700px",
          width: "100%",
          maxHeight: "90vh",
          overflowY: "auto",
          margin: "auto",
        }}
        onClick={(e) => e.stopPropagation()}
      >
        <div
          style={{
            display: "flex",
            justifyContent: "space-between",
            alignItems: "center",
            marginBottom: "24px",
          }}
        >
          <h2>{item ? "Edit Inventory Item" : "Add Inventory Item"}</h2>
          <button
            onClick={onClose}
            style={{
              background: "none",
              border: "none",
              fontSize: "24px",
              cursor: "pointer",
              padding: "4px",
            }}
          >
            <FiX />
          </button>
        </div>

        <form onSubmit={handleSubmit}>
          <div
            className={`form-group ${fieldErrors.product_name ? "has-error" : ""}`}
          >
            <label>Product Name *</label>
            <input
              type="text"
              value={formData.product_name}
              onChange={(e) => handleChange("product_name", e.target.value)}
              required
            />
            {fieldErrors.product_name && (
              <small className="field-error">{fieldErrors.product_name}</small>
            )}
          </div>

          <div
            style={{
              display: "grid",
              gridTemplateColumns: "repeat(auto-fit, minmax(250px, 1fr))",
              gap: "16px",
            }}
          >
            <div className="form-group">
              <label>Supplier</label>
              <input
                type="text"
                value={formData.supplier}
                onChange={(e) => handleChange("supplier", e.target.value)}
              />
            </div>

            <div
              className={`form-group ${fieldErrors.expiration_date ? "has-error" : ""}`}
            >
              <label>Batch Number</label>
              <input
                type="text"
                value={formData.batch_number}
                onChange={(e) => handleChange("batch_number", e.target.value)}
              />
            </div>
          </div>

          <div
            style={{
              display: "grid",
              gridTemplateColumns: "repeat(auto-fit, minmax(250px, 1fr))",
              gap: "16px",
            }}
          >
            <div className="form-group">
              <label>Date Added</label>
              <input
                type="datetime-local"
                value={formData.date_time_added}
                onChange={(e) =>
                  handleChange("date_time_added", e.target.value)
                }
              />
            </div>

            <div className="form-group">
              <label>Expiration Date</label>
              <input
                type="date"
                value={formData.expiration_date}
                onChange={(e) =>
                  handleChange("expiration_date", e.target.value)
                }
              />
              {fieldErrors.expiration_date && (
                <small className="field-error">
                  {fieldErrors.expiration_date}
                </small>
              )}
            </div>
          </div>

          <div
            style={{
              display: "grid",
              gridTemplateColumns: "repeat(auto-fit, minmax(250px, 1fr))",
              gap: "16px",
            }}
          >
            <div
              className={`form-group ${fieldErrors.units_bought ? "has-error" : ""}`}
            >
              <label>Units Bought *</label>
              <input
                type="number"
                step="0.01"
                min="0"
                value={formData.units_bought}
                onChange={(e) => handleChange("units_bought", e.target.value)}
                required
              />
              {fieldErrors.units_bought && (
                <small className="field-error">
                  {fieldErrors.units_bought}
                </small>
              )}
            </div>

            <div
              className={`form-group ${fieldErrors.units_sold ? "has-error" : ""}`}
            >
              <label>Units Sold *</label>
              <input
                type="number"
                step="0.01"
                min="0"
                max={formData.units_bought}
                value={formData.units_sold}
                onChange={(e) => handleChange("units_sold", e.target.value)}
                required
              />
              <small
                style={{
                  color: "var(--text-grey)",
                  fontSize: "12px",
                  marginTop: "4px",
                  display: "block",
                }}
              >
                Automatically updated from sales or manually entered if allowed.
                Must not exceed units bought.
              </small>
              {fieldErrors.units_sold && (
                <small className="field-error">{fieldErrors.units_sold}</small>
              )}
            </div>
          </div>

          <div
            style={{
              display: "grid",
              gridTemplateColumns: "repeat(auto-fit, minmax(250px, 1fr))",
              gap: "16px",
            }}
          >
            <div
              className={`form-group ${fieldErrors.low_stock_threshold ? "has-error" : ""}`}
            >
              <label>Unit Price (RWF) *</label>
              <input
                type="number"
                step="0.01"
                min="0"
                value={formData.unit_price}
                onChange={(e) => handleChange("unit_price", e.target.value)}
                required
              />
            </div>

            <div className="form-group">
              <label>Storage Location</label>
              <input
                type="text"
                value={formData.storage_location}
                onChange={(e) =>
                  handleChange("storage_location", e.target.value)
                }
              />
            </div>
          </div>

          <div
            style={{
              display: "grid",
              gridTemplateColumns: "repeat(auto-fit, minmax(250px, 1fr))",
              gap: "16px",
            }}
          >
            <div className="form-group">
              <label>Low Stock Threshold</label>
              <input
                type="number"
                min="1"
                value={formData.low_stock_threshold}
                onChange={(e) =>
                  handleChange("low_stock_threshold", e.target.value)
                }
              />
              <small
                style={{
                  color: "var(--text-grey)",
                  fontSize: "12px",
                  marginTop: "4px",
                  display: "block",
                }}
              >
                Triggers alert when remaining quantity falls below this value
              </small>
              {fieldErrors.low_stock_threshold && (
                <small className="field-error">
                  {fieldErrors.low_stock_threshold}
                </small>
              )}
            </div>

            <div
              className={`form-group ${fieldErrors.expiry_threshold_days ? "has-error" : ""}`}
            >
              <label>Expiry Threshold (days)</label>
              <input
                type="number"
                min="1"
                value={formData.expiry_threshold_days}
                onChange={(e) =>
                  handleChange("expiry_threshold_days", e.target.value)
                }
              />
              <small
                style={{
                  color: "var(--text-grey)",
                  fontSize: "12px",
                  marginTop: "4px",
                  display: "block",
                }}
              >
                Number of days before expiration to classify product as urgent
              </small>
              {fieldErrors.expiry_threshold_days && (
                <small className="field-error">
                  {fieldErrors.expiry_threshold_days}
                </small>
              )}
            </div>
          </div>

          <div className="form-group">
            <label>Notes</label>
            <textarea
              rows={3}
              value={formData.notes}
              onChange={(e) => handleChange("notes", e.target.value)}
            />
          </div>

          <div
            style={{
              display: "flex",
              gap: "12px",
              justifyContent: "flex-end",
              marginTop: "24px",
            }}
          >
            <button
              type="button"
              className="btn btn-secondary"
              onClick={onClose}
            >
              Cancel
            </button>
            <button type="submit" className="btn btn-primary" disabled={saving}>
              {saving ? "Saving..." : item ? "Update" : "Add"}
            </button>
          </div>
        </form>
      </div>
    </div>
  );
}

function BulkUploadErrorModal({ state, onClose }) {
  const { errors, createdCount, errorCount } = state || {};

  const handleDownloadReport = () => {
    if (!errors || errors.length === 0) return;
    const header = "row,field,message\n";
    const rows = errors
      .map((e) => {
        const safeMessage = String(e.message || "")
          .replace(/"/g, '""')
          .replace(/\r?\n/g, " ");
        return `${e.row},${e.field || ""},"${safeMessage}"`;
      })
      .join("\n");
    const csv = header + rows;
    const blob = new Blob([csv], { type: "text/csv;charset=utf-8;" });
    const url = URL.createObjectURL(blob);
    const link = document.createElement("a");
    link.href = url;
    link.download = "inventory_bulk_upload_errors.csv";
    link.click();
    URL.revokeObjectURL(url);
  };

  return (
    <div
      style={{
        position: "fixed",
        top: 0,
        left: 0,
        right: 0,
        bottom: 0,
        background: "rgba(0,0,0,0.5)",
        display: "flex",
        alignItems: "center",
        justifyContent: "center",
        zIndex: 1000,
        padding: "20px",
        overflowY: "auto",
      }}
      onClick={onClose}
    >
      <div
        className="card inventory-modal"
        style={{
          maxWidth: "800px",
          width: "100%",
          maxHeight: "90vh",
          overflowY: "auto",
          margin: "auto",
        }}
        onClick={(e) => e.stopPropagation()}
      >
        <div
          style={{
            display: "flex",
            justifyContent: "space-between",
            alignItems: "center",
            marginBottom: "16px",
          }}
        >
          <h2>Bulk Upload Validation Errors</h2>
          <button
            onClick={onClose}
            style={{
              background: "none",
              border: "none",
              fontSize: "24px",
              cursor: "pointer",
              padding: "4px",
            }}
          >
            <FiX />
          </button>
        </div>

        <p
          style={{
            marginBottom: "12px",
            fontSize: "14px",
            color: "var(--text-grey)",
          }}
        >
          {createdCount} row(s) created successfully, {errorCount} error(s)
          found.
        </p>

        <div
          style={{
            maxHeight: "50vh",
            overflowY: "auto",
            border: "1px solid var(--border-grey)",
            borderRadius: "8px",
            padding: "12px",
          }}
        >
          {errors && errors.length > 0 ? (
            <ul
              style={{
                listStyle: "none",
                padding: 0,
                margin: 0,
                fontSize: "13px",
              }}
            >
              {errors.map((err, idx) => (
                <li
                  key={`${err.row}-${err.field}-${idx}`}
                  style={{
                    padding: "6px 4px",
                    borderBottom: "1px solid var(--border-grey)",
                  }}
                >
                  <strong>Row {err.row}</strong> →{" "}
                  <span style={{ fontWeight: 500 }}>{err.field}</span>:{" "}
                  {err.message}
                </li>
              ))}
            </ul>
          ) : (
            <p>No detailed errors available.</p>
          )}
        </div>

        <div
          style={{
            display: "flex",
            justifyContent: "space-between",
            alignItems: "center",
            marginTop: "16px",
            gap: "12px",
          }}
        >
          <button
            type="button"
            className="btn btn-secondary btn-sm"
            onClick={handleDownloadReport}
          >
            <FiDownload /> Download Error Report
          </button>

          <div style={{ display: "flex", gap: "8px" }}>
            <button
              type="button"
              className="btn btn-secondary btn-sm"
              onClick={onClose}
            >
              Close
            </button>
          </div>
        </div>
      </div>
    </div>
  );
}

export default Inventory;
